import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

function formatYmd(ymd) {
  if (!ymd) return "—";
  const match = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(ymd);
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function daysLabel(daysLeft, t) {
  if (daysLeft < 0) {
    return t("EstimatesPage.contestacion.overdue", {
      count: Math.abs(daysLeft),
    });
  }
  if (daysLeft === 0) return t("EstimatesPage.contestacion.dueToday");
  return t("EstimatesPage.contestacion.dueIn", { count: daysLeft });
}

export function ContestacionCountBadge({ count }) {
  const { t } = useTranslation();
  if (!count) return null;
  return (
    <Box
      aria-label={t("EstimatesPage.contestacion.pendingAria", { count })}
      sx={{
        minWidth: 22,
        height: 22,
        px: 0.6,
        borderRadius: "50%",
        bgcolor: "#d32f2f",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.75rem",
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {count}
    </Box>
  );
}

export default function EstimateContestacionAvisos({
  items = [],
  onEntregar,
  onNoEntregar,
  compact = false,
}) {
  const [pendingId, setPendingId] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!items.length) return null;

  const run = async (id, action) => {
    if (pendingId) return;
    setPendingId(id);
    try {
      await action(id);
    } catch (error) {
      console.error(error);
      window.alert(t("EstimatesPage.contestacion.updateFailed"));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Stack
      spacing={compact ? 0.5 : 0.75}
      sx={{
        width: "100%",
        maxHeight: compact ? "none" : 320,
        overflow: compact ? "visible" : "auto",
      }}
    >
      {items.map((item) => {
        const title =
          item.designCliente || item.NombrePresupuesto || "Presupuesto";
        const busy = pendingId === item.IdPresupuesto;
        return (
          <Box
            key={item.IdPresupuesto}
            onClick={() =>
              navigate(`/estimates?idPresupuesto=${item.IdPresupuesto}`)
            }
            sx={(theme) => ({
              px: compact ? 0.85 : 1.25,
              py: compact ? 0.6 : 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha("#d32f2f", item.overdue ? 0.4 : 0.18),
              bgcolor: alpha("#d32f2f", item.overdue ? 0.08 : 0.04),
              display: "flex",
              flexWrap: "wrap",
              flexDirection: compact ? "column" : "row",
              alignItems: compact ? "stretch" : "center",
              justifyContent: "space-between",
              gap: compact ? 0.45 : 1,
              color: theme.palette.text.primary,
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#d32f2f", item.overdue ? 0.12 : 0.08),
              },
            })}
          >
            <Box sx={{ minWidth: 0, flex: compact ? "0 0 auto" : "1 1 220px" }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: compact ? "0.78rem" : "0.86rem",
                  }}
                  noWrap
                >
                  {item.ClienteNombre ? `${item.ClienteNombre} · ` : ""}
                  {title}
                </Typography>
                <Chip
                  size="small"
                  label={daysLabel(item.daysLeft, t)}
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    bgcolor: item.overdue ? "#d32f2f" : alpha("#d32f2f", 0.14),
                    color: item.overdue ? "#fff" : "#b71c1c",
                  }}
                />
              </Stack>
              <Typography
                sx={{
                  mt: compact ? 0.15 : 0.35,
                  fontSize: compact ? "0.68rem" : "0.75rem",
                  color: "text.secondary",
                  lineHeight: compact ? 1.3 : 1.45,
                }}
              >
                {item.NumeroPresupuesto ? `${item.NumeroPresupuesto} · ` : ""}
                {t("EstimatesPage.contestacion.line", {
                  date: formatYmd(item.FechaMaximaContestacion),
                })}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              data-contestacion-no-drag="1"
              onPointerDown={(event) => event.stopPropagation()}
              sx={{
                justifyContent: compact ? "flex-end" : "flex-start",
                cursor: "default",
                touchAction: "manipulation",
                "& .MuiButton-root": {
                  cursor: "pointer",
                },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  run(item.IdPresupuesto, onNoEntregar);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                  ...(compact
                    ? { minHeight: 28, py: 0.25, px: 1.1, fontSize: "0.72rem" }
                    : {}),
                }}
              >
                {t("EstimatesPage.contestacion.noDeliver")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  run(item.IdPresupuesto, onEntregar);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  cursor: "pointer",
                  bgcolor: "#d32f2f",
                  "&:hover": { bgcolor: "#b71c1c" },
                  ...(compact
                    ? {
                        minHeight: 28,
                        py: 0.25,
                        px: 1.2,
                        fontSize: "0.72rem",
                      }
                    : {}),
                }}
              >
                {t("EstimatesPage.contestacion.deliver")}
              </Button>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

const BANNER_POS_KEY = "crm.contestacionBanner.position";
const BANNER_OPEN_KEY = "crm.contestacionBanner.open";

function readBannerPosition() {
  try {
    const raw = localStorage.getItem(BANNER_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return null;
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

function readBannerOpen() {
  try {
    return localStorage.getItem(BANNER_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function clampBannerPosition(x, y, width, height) {
  const maxX = Math.max(8, window.innerWidth - width - 8);
  const maxY = Math.max(8, window.innerHeight - height - 8);
  return {
    x: Math.min(Math.max(8, x), maxX),
    y: Math.min(Math.max(8, y), maxY),
  };
}

export function EstimateContestacionGlobalBanner({
  items = [],
  onEntregar,
  onNoEntregar,
}) {
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [pos, setPos] = useState(readBannerPosition);
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(readBannerOpen);

  useEffect(() => {
    if (!pos || !boxRef.current) return undefined;
    const fit = () => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = clampBannerPosition(pos.x, pos.y, rect.width, rect.height);
      if (next.x === pos.x && next.y === pos.y) return;
      setPos(next);
      localStorage.setItem(BANNER_POS_KEY, JSON.stringify(next));
    };
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [pos, open]);

  if (!items.length || typeof document === "undefined") return null;

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(BANNER_OPEN_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    if (
      event.target?.closest?.(
        "button, a, input, textarea, select, [data-contestacion-no-drag]",
      )
    ) {
      return;
    }
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
      last: { x: rect.left, y: rect.top },
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 5) return;
    drag.moved = true;
    const next = clampBannerPosition(
      drag.originX + dx,
      drag.originY + dy,
      drag.width,
      drag.height,
    );
    drag.last = next;
    setDragging(true);
    setPos(next);
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (!drag.moved) return;
    suppressClickRef.current = true;
    localStorage.setItem(BANNER_POS_KEY, JSON.stringify(drag.last));
  };

  return createPortal(
    <Box
      ref={boxRef}
      role="status"
      aria-live="polite"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      sx={(theme) => ({
        position: "fixed",
        top: pos ? pos.y : "max(12px, env(safe-area-inset-top))",
        left: pos ? pos.x : "auto",
        right: pos ? "auto" : 16,
        zIndex: theme.zIndex.modal - 1,
        width: open ? "min(400px, calc(100vw - 24px))" : "auto",
        maxWidth: "min(400px, calc(100vw - 24px))",
        touchAction: "none",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
        "& button, & [data-contestacion-no-drag]": {
          cursor: "pointer",
          touchAction: "manipulation",
          userSelect: "auto",
        },
        "& [data-contestacion-no-drag]": {
          cursor: "default",
        },
        animation:
          pos || dragging
            ? "none"
            : "contestacionBannerIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes contestacionBannerIn": {
          from: { opacity: 0, transform: "translateY(-12px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      })}
    >
      <Box
        sx={(theme) => ({
          borderRadius: "16px",
          px: 0.85,
          pt: 0.45,
          pb: open ? 0.7 : 0.45,
          backdropFilter: "blur(18px) saturate(1.3)",
          WebkitBackdropFilter: "blur(18px) saturate(1.3)",
          bgcolor: alpha(
            theme.palette.background.paper,
            theme.palette.mode === "dark" ? 0.55 : 0.72,
          ),
          border: "1px solid",
          borderColor: alpha(
            theme.palette.common.white,
            theme.palette.mode === "dark" ? 0.12 : 0.55,
          ),
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
        })}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          onClick={(event) => {
            if (event.target?.closest?.("button")) return;
            toggleOpen();
          }}
          sx={{
            px: 0.35,
            py: 0.15,
            cursor: dragging ? "grabbing" : "grab",
            borderRadius: 1.5,
            "&:hover": { bgcolor: alpha("#d32f2f", 0.06) },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#d32f2f",
              boxShadow: "0 0 0 4px rgba(211,47,47,0.16)",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              flex: 1,
              whiteSpace: "nowrap",
            }}
          >
            {t("EstimatesPage.contestacion.title")}
          </Typography>
          <ContestacionCountBadge count={items.length} />
          <IconButton
            size="small"
            aria-label={
              open
                ? t("EstimatesPage.contestacion.collapse")
                : t("EstimatesPage.contestacion.expand")
            }
            onClick={(event) => {
              event.stopPropagation();
              toggleOpen();
            }}
            sx={{ p: 0.25 }}
          >
            {open ? (
              <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Stack>
        <Collapse in={open} unmountOnExit>
          <Box
            data-contestacion-no-drag="1"
            onPointerDown={(event) => event.stopPropagation()}
            sx={{
              mt: 0.45,
              maxHeight: "min(42vh, 360px)",
              overflow: "auto",
              pr: 0.15,
              cursor: "default",
              touchAction: "manipulation",
            }}
          >
            <EstimateContestacionAvisos
              compact
              items={items}
              onEntregar={onEntregar}
              onNoEntregar={onNoEntregar}
            />
          </Box>
        </Collapse>
      </Box>
    </Box>,
    document.body,
  );
}
